/**
 * /admin/lpu/:code/access-matrix — Матрица доступов по ЛПУ.
 *
 * Показывает:
 * - Список групп доступа с количеством участников и RLS-паттерном
 * - Таблицу пользователей с 4 колонками (fil/podr/srv/otd) + предупреждения
 *
 * Source: design-rls-admin-ui-2026-04-12.md section 1
 */

import { useParams, useNavigate } from 'react-router-dom'
import {
  Alert,
  Breadcrumb,
  Button,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  Badge,
} from 'antd'
import {
  ReloadOutlined,
  TableOutlined,
  WarningOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'

import { useLpuAccessMatrix } from '@/hooks/useLpuAccessMatrix'
import type { AccessGroupItem, AccessMatrixUser } from '@/api/lpuAccessMatrix'

const { Title, Text } = Typography

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Человекочитаемые метки уровней доступа */
const ACCESS_LEVEL_LABELS: Record<string, string> = {
  full: 'Полный (все данные ЛПУ)',
  fil: 'Филиал (только свой)',
  podr: 'Подразделение (только своё)',
  srv: 'Служба (только своя)',
  otd: 'Отделение (только своё)',
  unknown: 'Нестандартный',
}

const ACCESS_LEVEL_COLORS: Record<string, string> = {
  full: 'green',
  fil: 'blue',
  podr: 'cyan',
  srv: 'purple',
  otd: 'orange',
  unknown: 'default',
}

/** Отображение RLS-привязки: '*' → 'ALL', null → '—', иначе код */
function bindingCell(value: string | null): React.ReactNode {
  if (value === null) {
    return <Text type="secondary">—</Text>
  }
  if (value === '*') {
    return <Tag color="green">ALL</Tag>
  }
  // Несколько значений через запятую
  const parts = value.split(', ')
  if (parts.length === 1) {
    return <Tag color="blue">{value}</Tag>
  }
  return (
    <Space wrap size={4}>
      {parts.map((p) => (
        <Tag key={p} color="blue" style={{ marginBottom: 2 }}>
          {p}
        </Tag>
      ))}
    </Space>
  )
}

/** RLS-паттерн колонки: 'all' → зелёный ALL, 'userid' → жёлтый userid */
function patternCell(val: string): React.ReactNode {
  if (val === 'all') return <Tag color="green">ALL</Tag>
  if (val === 'userid') return <Tag color="gold">userid</Tag>
  return <Tag>{val}</Tag>
}

// ── Groups table ──────────────────────────────────────────────────────────────

const GROUP_COLUMNS: ColumnsType<AccessGroupItem> = [
  {
    title: 'Группа (role key)',
    dataIndex: 'zitadel_role_key',
    key: 'zitadel_role_key',
    render: (v: string) => <Text code>{v}</Text>,
    width: 220,
  },
  {
    title: 'Название',
    dataIndex: 'name',
    key: 'name',
    ellipsis: true,
  },
  {
    title: 'Участников',
    dataIndex: 'members_count',
    key: 'members_count',
    align: 'center' as const,
    width: 110,
    sorter: (a: AccessGroupItem, b: AccessGroupItem) => a.members_count - b.members_count,
  },
  {
    title: 'Уровень доступа',
    dataIndex: 'access_level',
    key: 'access_level',
    width: 220,
    render: (level: string) => (
      <Tag color={ACCESS_LEVEL_COLORS[level] ?? 'default'}>
        {ACCESS_LEVEL_LABELS[level] ?? level}
      </Tag>
    ),
  },
  {
    title: 'Филиал',
    key: 'pat_fil',
    width: 90,
    align: 'center' as const,
    render: (_: unknown, r: AccessGroupItem) => patternCell(r.rls_pattern.fil),
  },
  {
    title: 'Подр.',
    key: 'pat_podr',
    width: 90,
    align: 'center' as const,
    render: (_: unknown, r: AccessGroupItem) => patternCell(r.rls_pattern.podr),
  },
  {
    title: 'Служба',
    key: 'pat_srv',
    width: 90,
    align: 'center' as const,
    render: (_: unknown, r: AccessGroupItem) => patternCell(r.rls_pattern.srv),
  },
  {
    title: 'Отделение',
    key: 'pat_otd',
    width: 100,
    align: 'center' as const,
    render: (_: unknown, r: AccessGroupItem) => patternCell(r.rls_pattern.otd),
  },
]

// ── Users table ───────────────────────────────────────────────────────────────

const USER_COLUMNS: ColumnsType<AccessMatrixUser> = [
  {
    title: 'Пользователь',
    key: 'user',
    width: 220,
    render: (_: unknown, r: AccessMatrixUser) => (
      <Space>
        {r.warnings.length > 0 && (
          <Tooltip
            title={r.warnings.map((w) => WARNING_LABELS[w] ?? w).join('; ')}
          >
            <WarningOutlined style={{ color: '#faad14' }} />
          </Tooltip>
        )}
        <div>
          <div>
            <Text strong>{r.display_name}</Text>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.email}
          </Text>
        </div>
      </Space>
    ),
  },
  {
    title: 'Группы',
    dataIndex: 'groups',
    key: 'groups',
    ellipsis: true,
    render: (groups: string[]) => (
      <Space wrap size={4}>
        {groups.length > 0 ? (
          groups.map((g) => (
            <Tag key={g} color="geekblue" style={{ marginBottom: 2 }}>
              {g}
            </Tag>
          ))
        ) : (
          <Text type="secondary">нет групп</Text>
        )}
      </Space>
    ),
  },
  {
    title: 'Филиал',
    key: 'fil',
    align: 'center' as const,
    width: 100,
    render: (_: unknown, r: AccessMatrixUser) => bindingCell(r.rls_bindings.fil),
  },
  {
    title: 'Подр.',
    key: 'podr',
    align: 'center' as const,
    width: 100,
    render: (_: unknown, r: AccessMatrixUser) => bindingCell(r.rls_bindings.podr),
  },
  {
    title: 'Служба',
    key: 'srv',
    align: 'center' as const,
    width: 100,
    render: (_: unknown, r: AccessMatrixUser) => bindingCell(r.rls_bindings.srv),
  },
  {
    title: 'Отделение',
    key: 'otd',
    align: 'center' as const,
    width: 130,
    render: (_: unknown, r: AccessMatrixUser) => bindingCell(r.rls_bindings.otd),
  },
  {
    title: 'CH',
    key: 'ch',
    align: 'center' as const,
    width: 60,
    render: (_: unknown, r: AccessMatrixUser) =>
      r.ch_rls_access_exists ? (
        <Tooltip title="Запись в CH rls_access есть">
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
        </Tooltip>
      ) : (
        <Tooltip title="Нет записи в CH rls_access">
          <Text type="secondary">—</Text>
        </Tooltip>
      ),
  },
]

const WARNING_LABELS: Record<string, string> = {
  no_ch_rls_record: 'Нет записи в CH rls_access — userid-RLS вернёт 0 строк',
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function LpuAccessMatrixPage() {
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const { data, isLoading, isError, error, refetch } = useLpuAccessMatrix(code)

  const warningUsers = data?.users.filter((u) => u.warnings.length > 0) ?? []

  return (
    <div>
      <Breadcrumb
        items={[
          { title: 'Администрирование' },
          {
            title: 'Здоровье доступов',
            onClick: () => navigate('/admin/access-health'),
            className: 'ant-breadcrumb-link',
          },
          { title: code ? `ЛПУ ${code} — Матрица доступов` : 'Матрица доступов' },
        ]}
        style={{ marginBottom: 16 }}
      />

      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <Space>
          <TableOutlined style={{ fontSize: 20 }} />
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Матрица доступов ЛПУ
            </Title>
            {data && (
              <Text type="secondary" style={{ fontSize: 13 }}>
                {data.lpu_name}
                {' '}
                <Tag>{data.lpu_code}</Tag>
              </Text>
            )}
          </div>
        </Space>
        <Button
          icon={<ReloadOutlined spin={isLoading} />}
          onClick={() => refetch()}
          loading={isLoading}
        >
          Обновить
        </Button>
      </div>

      {/* Error */}
      {isError && (
        <Alert
          type="error"
          showIcon
          message="Ошибка загрузки данных"
          description={
            (error as any)?.response?.data?.detail ||
            (error as Error)?.message ||
            'Неизвестная ошибка'
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {isLoading && !data ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" tip="Загружаем матрицу доступов..." />
        </div>
      ) : (
        <>
          {/* Warning banner */}
          {warningUsers.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                <>
                  <strong>{warningUsers.length}</strong> пользовател
                  {warningUsers.length === 1 ? 'ь' : 'ей'} с предупреждениями —
                  userid-RLS вернёт пустые дашборды
                </>
              }
              description={
                <Text style={{ fontSize: 12 }}>
                  Пользователи без записи в CH rls_access помечены ⚠️.
                  Необходима синхронизация (деплой access-config).
                </Text>
              }
            />
          )}

          {/* Groups section */}
          <div style={{ marginBottom: 28 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 12,
              }}
            >
              <Title level={5} style={{ margin: 0 }}>
                Группы доступа
              </Title>
              {data && (
                <Badge
                  count={data.groups.length}
                  style={{ backgroundColor: '#1677ff' }}
                />
              )}
            </div>
            <Table<AccessGroupItem>
              dataSource={data?.groups ?? []}
              columns={GROUP_COLUMNS}
              rowKey="zitadel_role_key"
              loading={isLoading}
              size="small"
              pagination={false}
              locale={{ emptyText: 'Нет групп доступа для этого ЛПУ' }}
            />
          </div>

          {/* Users section */}
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 12,
              }}
            >
              <Title level={5} style={{ margin: 0 }}>
                Персональные RLS-привязки
              </Title>
              {data && (
                <Badge
                  count={data.users.length}
                  style={{ backgroundColor: '#1677ff' }}
                />
              )}
              {warningUsers.length > 0 && (
                <Badge
                  count={`⚠️ ${warningUsers.length}`}
                  style={{ backgroundColor: '#faad14' }}
                />
              )}
            </div>

            <Alert
              type="info"
              showIcon={false}
              style={{ marginBottom: 10, fontSize: 12 }}
              message={
                <Text style={{ fontSize: 12 }}>
                  <strong>ALL</strong> = доступ ко всем объектам поля
                  &nbsp;·&nbsp;
                  <strong>—</strong> = нет персональной привязки (применяется
                  групповой паттерн)
                  &nbsp;·&nbsp;
                  <strong>⚠️</strong> = нет записи в CH (userid-RLS сломан)
                </Text>
              }
            />

            <Table<AccessMatrixUser>
              dataSource={data?.users ?? []}
              columns={USER_COLUMNS}
              rowKey="email"
              loading={isLoading}
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true }}
              locale={{ emptyText: 'Нет пользователей в этом ЛПУ' }}
              rowClassName={(record) =>
                record.warnings.length > 0 ? 'ant-table-row-warning' : ''
              }
            />
          </div>
        </>
      )}
    </div>
  )
}
